import { inject, Injectable } from '@angular/core';
import { BaseService } from '@shared/core';
import { map, Observable } from 'rxjs';
import {
  IAssignControllerRequest,
  ICreateLanePayload,
  IGateItem,
  IGateParams,
  IGatePayload,
  IGateResponseModel,
} from '@pkp-g/core';
import {
  IBulkGateRequest,
  IBulkGateResult,
  IManualPlateRequest,
  IOpenGateRequest,
  IRecognizePlateResult,
} from '../../arm/models/gate-ops.model';

@Injectable({
  providedIn: 'root',
})
export class GatesApiService {
  private http = inject(BaseService);
  // Срез 04: gate-алиас `v1/gates` удалён на бэкенде — LaneController отдаёт тот же
  // CRUD/операции по каноническому маршруту `v1/lanes`.
  apiUrl = 'v1/lanes';

  getAllGates(filter?: IGateParams): Observable<IGateResponseModel> {
    return this.http.getWithHeaders(this.apiUrl, filter).pipe(
      map(res => ({
        items: res.body ?? [],
        total: Number(res.headers.get('X-Pagination-Total') ?? 0),
      })),
    );
  }

  setGate(payload: IGatePayload): Observable<unknown> {
    return this.http.post(this.apiUrl, payload);
  }

  /** Создать полосу (CreateLaneCommand) — конструктор «Схема объекта». Возвращает id полосы. */
  createLane(payload: ICreateLanePayload): Observable<string> {
    return this.http.post(this.apiUrl, payload);
  }

  getGateById(id: string): Observable<IGateItem> {
    return this.http.get(this.apiUrl + '/' + id);
  }

  updateGateById(id: string, payload: IGatePayload): Observable<unknown> {
    return this.http.put(this.apiUrl + '/' + id, payload);
  }

  deleteGate(id: string): Observable<unknown> {
    return this.http.delete(this.apiUrl + '/' + id);
  }

  // Barrier control (Phase 0, BE-0.3 + Phase 1, BE-1.2/1.3/1.4). Relative URLs ->
  // BaseService prefixes apiUrl; auth interceptor attaches the Bearer token.

  /**
   * Manual open with reason + comment (Phase 1) -> logged as ManualGateOpen. The body is
   * optional: Phase 0 call-sites that pass nothing get a default OTHER reason on the
   * backend, keeping the Phase 0 contract intact.
   */
  openGate(id: string, body?: IOpenGateRequest): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/open', body ?? {});
  }

  /** Open without comments (logged with withoutComment=true). */
  openGateNoComment(id: string, plateNumber?: string): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/open-no-comment', { plateNumber });
  }

  closeGate(id: string): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/close', {});
  }

  /** Fix the barrier open (true) or closed (false). */
  fixGate(id: string, open: boolean): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/fix', { open });
  }

  /** Release the fix (back to CLOSED). */
  unfixGate(id: string): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/unfix', {});
  }

  /** Emergency-open all barriers (or a subset by gateIds / parkingLotId). */
  emergencyOpen(body?: IBulkGateRequest): Observable<IBulkGateResult> {
    return this.http.post(this.apiUrl + '/emergency-open', body ?? {});
  }

  /** Fix all barriers (FIXED_OPEN by default). */
  fixAll(body?: IBulkGateRequest): Observable<IBulkGateResult> {
    return this.http.post(this.apiUrl + '/fix-all', body ?? {});
  }

  /**
   * Manual plate "Впустить"/"Выпустить": runs the normal entry/exit flow for the typed
   * plate. The verdict/overlay arrives asynchronously via camera-stream SignalR.
   */
  manualPlate(id: string, body: IManualPlateRequest): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + id + '/manual-plate', body);
  }

  /** Recognize plate by trigger (operator button): no request body, direction from Lane.Type. */
  recognizePlate(id: string): Observable<IRecognizePlateResult> {
    return this.http.post(this.apiUrl + '/' + id + '/recognize-plate', {});
  }

  // Привязка контроллера к шлагбауму (право lanes:manage).

  /** Привязать устройство-контроллер к шлагбауму. 409, если уже привязан и replace!=true. */
  assignController(gateId: string, body: IAssignControllerRequest): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + gateId + '/controller', body);
  }

  /** Отвязать контроллер от шлагбаума. */
  unassignController(gateId: string): Observable<unknown> {
    return this.http.delete(this.apiUrl + '/' + gateId + '/controller');
  }

  /** LA1: привязать любое устройство (любого класса) к полосе. */
  attachDevice(laneId: string, deviceId: string): Observable<unknown> {
    return this.http.post(this.apiUrl + '/' + laneId + '/devices/' + deviceId, {});
  }

  /** LA1: отвязать устройство от полосы. */
  detachDevice(laneId: string, deviceId: string): Observable<unknown> {
    return this.http.delete(this.apiUrl + '/' + laneId + '/devices/' + deviceId);
  }
}
